import * as THREE from "three";

export type ScalePreset = "custom" | 10 | 20 | 50 | 100 | 200 | 500;

export type ExportFormatId =
  | "a4-portrait"
  | "a4-landscape"
  | "a3-portrait"
  | "a3-landscape"
  | "desktop"
  | "instagram";

export interface ExportFormatOption {
  id: ExportFormatId;
  label: string;
  format: string;
  orientation: "portrait" | "landscape";
  width: number;
  height: number;
  paperWidthMm?: number;
  paperHeightMm?: number;
  supportsScale: boolean;
}

export interface PreviewSize {
  width: number;
  height: number;
}

export interface DrawableSize {
  width: number;
  height: number;
}

export interface ExportCaptureMetrics {
  captureBox: THREE.Box3;
  captureWidthWorld: number;
  captureHeightWorld: number;
  unitsPerPixel: number;
  sceneCenter: THREE.Vector3;
}

export interface ResolvedExportScale {
  preset: ScalePreset;
  exactPreset: Exclude<ScalePreset, "custom"> | null;
  scaleDenominator: number | null;
  captureWidthWorld: number;
  captureHeightWorld: number;
}

export const EXPORT_SCALE_MATCH_EPSILON = 1e-3;
export const EXPORT_SCALE_PRESETS: Exclude<ScalePreset, "custom">[] = [10, 20, 50, 100, 200, 500];

export const EXPORT_FORMAT_OPTIONS: ExportFormatOption[] = [
  {
    id: "a4-portrait",
    label: "A4 portrait",
    format: "a4",
    orientation: "portrait",
    width: 1240,
    height: 1754,
    paperWidthMm: 210,
    paperHeightMm: 297,
    supportsScale: true,
  },
  {
    id: "a4-landscape",
    label: "A4 landscape",
    format: "a4",
    orientation: "landscape",
    width: 1754,
    height: 1240,
    paperWidthMm: 297,
    paperHeightMm: 210,
    supportsScale: true,
  },
  {
    id: "a3-portrait",
    label: "A3 portrait",
    format: "a3",
    orientation: "portrait",
    width: 1754,
    height: 2480,
    paperWidthMm: 297,
    paperHeightMm: 420,
    supportsScale: true,
  },
  {
    id: "a3-landscape",
    label: "A3 landscape",
    format: "a3",
    orientation: "landscape",
    width: 2480,
    height: 1754,
    paperWidthMm: 420,
    paperHeightMm: 297,
    supportsScale: true,
  },
  {
    id: "desktop",
    label: "Desktop format",
    format: "a4",
    orientation: "landscape",
    width: 1754,
    height: 1240,
    supportsScale: false,
  },
  {
    id: "instagram",
    label: "Square format",
    format: "a4",
    orientation: "portrait",
    width: 600,
    height: 600,
    supportsScale: false,
  },
];

export const getExportFormatOption = (formatId: ExportFormatId): ExportFormatOption => {
  const format = EXPORT_FORMAT_OPTIONS.find((option) => option.id === formatId);
  if (!format) {
    throw new Error(`Unknown export format: ${formatId}`);
  }
  return format;
};

export const getPreviewSize = (
  format: Pick<ExportFormatOption, "width" | "height">,
  maxDimension = 400,
): PreviewSize => {
  const aspectRatio = format.width / format.height;

  if (aspectRatio > 1) {
    return {
      width: maxDimension,
      height: maxDimension / aspectRatio,
    };
  }

  return {
    width: maxDimension * aspectRatio,
    height: maxDimension,
  };
};

export const getDrawablePixelSize = (
  format: Pick<ExportFormatOption, "width" | "height" | "supportsScale">,
): DrawableSize => ({
  width: format.width,
  height: format.height,
});

export const getDrawablePaperSizeMm = (
  format: Pick<ExportFormatOption, "paperWidthMm" | "paperHeightMm" | "supportsScale">,
): DrawableSize | null => {
  if (!format.supportsScale || !format.paperWidthMm || !format.paperHeightMm) {
    return null;
  }

  return {
    width: format.paperWidthMm,
    height: format.paperHeightMm,
  };
};

export const MIN_EXPORT_ZOOM = 0.05;
export const MAX_EXPORT_ZOOM = 20;

export const clampExportZoom = (zoom: number) => Math.max(MIN_EXPORT_ZOOM, Math.min(zoom, MAX_EXPORT_ZOOM));

export const computeExportCaptureMetrics = (
  sceneBox: THREE.Box3,
  viewSize: DrawableSize,
  zoom: number,
  pan: { x: number; y: number },
): ExportCaptureMetrics | null => {
  if (sceneBox.isEmpty() || viewSize.width <= 0 || viewSize.height <= 0) {
    return null;
  }

  const sceneCenter = new THREE.Vector3();
  sceneBox.getCenter(sceneCenter);

  const size = new THREE.Vector3();
  sceneBox.getSize(size);

  if (!Number.isFinite(size.x) || !Number.isFinite(size.y) || size.x <= 0 || size.y <= 0) {
    return null;
  }

  const aspectX = size.x / viewSize.width;
  const aspectY = size.y / viewSize.height;
  const unitsPerPixel = Math.max(aspectX, aspectY) / zoom;
  const captureWidth = viewSize.width * unitsPerPixel;
  const captureHeight = viewSize.height * unitsPerPixel;
  const adjustX = pan.x * unitsPerPixel;
  const adjustY = pan.y * unitsPerPixel;

  return {
    captureBox: new THREE.Box3(
      new THREE.Vector3(
        sceneCenter.x - captureWidth / 2 - adjustX,
        sceneCenter.y - captureHeight / 2 + adjustY,
        sceneBox.min.z,
      ),
      new THREE.Vector3(
        sceneCenter.x + captureWidth / 2 - adjustX,
        sceneCenter.y + captureHeight / 2 + adjustY,
        sceneBox.max.z,
      ),
    ),
    captureWidthWorld: captureWidth,
    captureHeightWorld: captureHeight,
    unitsPerPixel,
    sceneCenter,
  };
};

export const getExactScalePreset = (scaleDenominator: number | null): Exclude<ScalePreset, "custom"> | null => {
  if (scaleDenominator === null) {
    return null;
  }

  for (const preset of EXPORT_SCALE_PRESETS) {
    if (Math.abs(scaleDenominator - preset) <= EXPORT_SCALE_MATCH_EPSILON) {
      return preset;
    }
  }

  return null;
};

export const resolveExportScale = (
  format: Pick<ExportFormatOption, "supportsScale" | "paperWidthMm" | "paperHeightMm">,
  captureWidthWorld: number,
  captureHeightWorld: number,
): ResolvedExportScale => {
  const drawablePaperSize = getDrawablePaperSizeMm(format);
  if (!drawablePaperSize) {
    return {
      preset: "custom",
      exactPreset: null,
      scaleDenominator: null,
      captureWidthWorld,
      captureHeightWorld,
    };
  }

  const widthScale = (captureWidthWorld * 1000) / drawablePaperSize.width;
  const heightScale = (captureHeightWorld * 1000) / drawablePaperSize.height;
  const scaleDenominator = Math.max(widthScale, heightScale);
  const exactPreset = getExactScalePreset(scaleDenominator);

  return {
    preset: exactPreset ?? "custom",
    exactPreset,
    scaleDenominator,
    captureWidthWorld,
    captureHeightWorld,
  };
};

export const getTargetZoomForScale = (
  sceneBox: THREE.Box3,
  format: ExportFormatOption,
  viewSize: DrawableSize,
  targetScalePreset: Exclude<ScalePreset, "custom">,
): number | null =>
  getTargetZoomForScaleDenominator(sceneBox, format, viewSize, targetScalePreset);

export const getTargetZoomForScaleDenominator = (
  sceneBox: THREE.Box3,
  format: ExportFormatOption,
  viewSize: DrawableSize,
  targetScaleDenominator: number,
): number | null => {
  const drawablePaperSize = getDrawablePaperSizeMm(format);
  if (
    !drawablePaperSize ||
    sceneBox.isEmpty() ||
    !Number.isFinite(targetScaleDenominator) ||
    targetScaleDenominator <= 0
  ) {
    return null;
  }

  const size = new THREE.Vector3();
  sceneBox.getSize(size);
  if (!Number.isFinite(size.x) || !Number.isFinite(size.y) || size.x <= 0 || size.y <= 0) {
    return null;
  }

  const baseUnitsPerPixel = Math.max(size.x / viewSize.width, size.y / viewSize.height);
  const targetCaptureWidthWorld = (targetScaleDenominator * drawablePaperSize.width) / 1000;
  const targetCaptureHeightWorld = (targetScaleDenominator * drawablePaperSize.height) / 1000;
  const targetUnitsPerPixel = Math.max(
    targetCaptureWidthWorld / viewSize.width,
    targetCaptureHeightWorld / viewSize.height,
  );

  if (!Number.isFinite(baseUnitsPerPixel) || !Number.isFinite(targetUnitsPerPixel) || targetUnitsPerPixel <= 0) {
    return null;
  }

  return clampExportZoom(baseUnitsPerPixel / targetUnitsPerPixel);
};

export const formatScaleLabel = (resolvedScale: Pick<ResolvedExportScale, "scaleDenominator">): string => {
  if (resolvedScale.scaleDenominator === null) {
    return "Custom";
  }

  return `1:${Math.round(resolvedScale.scaleDenominator)}`;
};

export const formatExportDate = (updatedAt?: string | null): string => {
  if (!updatedAt) {
    return "No date";
  }

  const parsedDate = new Date(updatedAt);
  if (Number.isNaN(parsedDate.getTime())) {
    return "No date";
  }

  return new Intl.DateTimeFormat("sv-SE").format(parsedDate);
};
